import { useAuth } from '../contexts/AuthContext';
import type { LoginResponse } from '@pgway/shared';

export type Tier = 'super' | 'standard' | 'manager' | null;

type MaybeUser = LoginResponse['user'] | null | undefined;

/**
 * Resolves an admin's effective tier WITHOUT failing open.
 *
 * A scoped admin (standard/manager) always has a companyId; the bootstrap
 * superadmin has none. So when adminTier is absent (old backend / stale
 * session / legacy doc) we infer from companyId instead of defaulting to
 * 'super' — defaulting to super was causing scoped admins to be treated as
 * superadmins, which then showed them an empty company/seller list.
 * Backend `loadAdminScope` is always the real authority; this only drives UI.
 */
export function resolveTier(user: MaybeUser): Tier {
  if (!user || user.role !== 'admin') return null;
  if (user.adminTier === 'super' || user.adminTier === 'standard' || user.adminTier === 'manager') {
    return user.adminTier;
  }
  // adminTier missing: company-less => bootstrap super; company-scoped =>
  // assume the most restrictive tier (manager) so the UI never over-grants.
  return user.companyId ? 'manager' : 'super';
}

/**
 * Current admin's tier + derived capability flags. Non-admin roles get
 * tier=null and all flags false.
 *
 * - canManage:       mutate sellers / sub-sellers / fees (super or standard)
 * - canCompanyAdmin: company CRUD, API config, user management (super only)
 * - canCleanupStats: destructive stats cleanup (super or standard; not manager)
 */
export function useTier() {
  const { user } = useAuth();
  const tier = resolveTier(user);

  return {
    tier,
    isSuper: tier === 'super',
    isStandard: tier === 'standard',
    isManager: tier === 'manager',
    canManage: tier === 'super' || tier === 'standard',
    canCompanyAdmin: tier === 'super',
    canCleanupStats: tier === 'super' || tier === 'standard',
  };
}
